import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../admin/AdminContext';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebaseClient';

import './AdminLogin.css';

export default function AdminLogin() {
    const [user, setUser] = useState('');
    const [pass, setPass] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { loginWithIdToken } = useAdmin() as any;
    const navigate = useNavigate();

    const friendlyError = (err: any) => {
        if (!err) return 'Sign-in failed';
        // Firebase error codes are like 'auth/invalid-email'
        const code = err.code || '';
        switch (code) {
            case 'auth/invalid-email':
                return 'Please enter a valid email address.';
            case 'auth/user-not-found':
                return 'No account found for that email.';
            case 'auth/wrong-password':
                return 'Incorrect password. Please try again.';
            case 'auth/network-request-failed':
                return 'Network error. Check your connection and try again.';
            default:
                // If the error has a short message that's user-friendly, use it; otherwise generic.
                const msg = (err.message || '').toString();
                if (msg && !msg.toLowerCase().includes('firebase')) return msg;
                return 'Sign-in failed. Please verify your credentials.';
        }
    };

    const submit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (isLoading) return;

        setError(null);
        setIsLoading(true);

        try {
            const cred = await signInWithEmailAndPassword(auth, user, pass);
            const idToken = await cred.user.getIdToken();
            const ok = await loginWithIdToken(idToken);
            if (ok) navigate('/admin');
            else setError('Not authorized');
        } catch (err: any) {
            console.error('Sign-in error', err);
            setError(friendlyError(err));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-accent" aria-hidden="true"></div>

                <div className="login-header">
                    <div className="login-logo">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 2L2 7l10 5 10-5-10-5z" />
                            <path d="M2 17l10 5 10-5" />
                            <path d="M2 12l10 5 10-5" />
                        </svg>
                    </div>
                    <h1 className="login-title">Welcome Back</h1>
                    <p className="login-subtitle">Sign in to access your admin dashboard</p>
                </div>

                <form onSubmit={submit} className="login-form">
                    <div className="input-group">
                        <label htmlFor="email" className="input-label">Email</label>
                        <input
                            id="email"
                            value={user}
                            onChange={(e) => setUser(e.target.value)}
                            placeholder="admin@example.com"
                            autoComplete="email"
                            type="email"
                            className="login-input"
                            disabled={isLoading}
                        />
                    </div>

                    <div className="input-group">
                        <label htmlFor="password" className="input-label">Password</label>
                        <input
                            id="password"
                            value={pass}
                            onChange={(e) => setPass(e.target.value)}
                            placeholder="••••••••"
                            type="password"
                            autoComplete="current-password"
                            className="login-input"
                            disabled={isLoading}
                        />
                    </div>

                    {error && (
                        <div className="login-error">
                            <svg viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        className={`login-button ${isLoading ? 'loading' : ''}`}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <span className="spinner"></span>
                                Signing in...
                            </>
                        ) : (
                            'Sign in'
                        )}
                    </button>
                </form>

                <div className="login-footer">
                    Need access? Contact the site owner.
                </div>
            </div>
        </div>
    );
}
